import axios from "axios";

import {requiredHeaders} from "../../helpers/authservice/requiredHeaders";
import {apiRequestsHelper} from "@/state/pinia/apiRequestHelper";
import {log} from "./log";
import {defineStore} from "pinia";
import {useLazyLoaderModule} from "@/state/pinia/lazyloaderManagerModule";

const visualReportsEndpoint = "/api/visual-reports";

export const useVisualReportsModule = defineStore("visualReportsModule", {

    state: () => ({
        lazyLoaderModule: useLazyLoaderModule(),

        // Saved reports list
        visualReportsList: [],
        visualReportsListPagesCount: 0,
        visualReportsListTotal: 0,

        // Available report types
        availableVisualReportTypes: [],

        // View config for current report
        visualReportViewConfig: null,

        // Rows data
        visualReportRows: [],
        visualReportRowsPagesCount: 0,
        visualReportRowsTotal: 0,

        // Chart config and data for current report
        visualReportChartConfig: null,
        visualReportChartData: null,

        // Aggregation metadata and aggregated rows
        visualReportAggregationMetadata: null,
        visualReportAggregatedRows: [],
        visualReportAggregatedRowsTotal: 0,
        visualReportAggregatedRowsPagesCount: 0,
        visualReportAggregatedRowsCurrentPage: 1,
    }),

    getters: {
        getVisualReportsList: (state) => state.visualReportsList,
        getVisualReportsListPagesCount: (state) => state.visualReportsListPagesCount,
        getVisualReportsListTotal: (state) => state.visualReportsListTotal,

        getAvailableVisualReportTypes: (state) => state.availableVisualReportTypes,

        getVisualReportViewConfig: (state) => state.visualReportViewConfig,

        getVisualReportRows: (state) => state.visualReportRows,
        getVisualReportRowsPagesCount: (state) => state.visualReportRowsPagesCount,
        getVisualReportRowsTotal: (state) => state.visualReportRowsTotal,

        getVisualReportChartConfig: (state) => state.visualReportChartConfig,
        getVisualReportChartData: (state) => state.visualReportChartData,

        getVisualReportAggregationMetadata: (state) => state.visualReportAggregationMetadata,
        getVisualReportAggregatedRows: (state) => state.visualReportAggregatedRows,
        getVisualReportAggregatedRowsTotal: (state) => state.visualReportAggregatedRowsTotal,
        getVisualReportAggregatedRowsPagesCount: (state) => state.visualReportAggregatedRowsPagesCount,
        getVisualReportAggregatedRowsCurrentPage: (state) => state.visualReportAggregatedRowsCurrentPage,
    },

    actions: {
        // mutations
        setVisualReportsList(list) {
            this.visualReportsList = list;
        },
        setVisualReportsListPagesCount(pagesCount) {
            this.visualReportsListPagesCount = pagesCount;
        },
        setVisualReportsListTotal(total) {
            this.visualReportsListTotal = total;
        },
        setAvailableVisualReportTypes(types) {
            this.availableVisualReportTypes = types;
        },
        setVisualReportViewConfig(config) {
            this.visualReportViewConfig = config;
        },
        setVisualReportRows(rows) {
            this.visualReportRows = rows;
        },
        setVisualReportRowsPagesCount(pagesCount) {
            this.visualReportRowsPagesCount = pagesCount;
        },
        setVisualReportRowsTotal(total) {
            this.visualReportRowsTotal = total;
        },
        setVisualReportChartConfig(config) {
            this.visualReportChartConfig = config;
        },
        setVisualReportChartData(data) {
            this.visualReportChartData = data;
        },
        setVisualReportAggregationMetadata(metadata) {
            this.visualReportAggregationMetadata = metadata;
        },
        setVisualReportAggregatedRows(rows) {
            this.visualReportAggregatedRows = rows;
        },
        setVisualReportAggregatedRowsTotal(total) {
            this.visualReportAggregatedRowsTotal = total;
        },
        setVisualReportAggregatedRowsPagesCount(pagesCount) {
            this.visualReportAggregatedRowsPagesCount = pagesCount;
        },
        setVisualReportAggregatedRowsCurrentPage(page) {
            this.visualReportAggregatedRowsCurrentPage = page;
        },

        // ======================= Report Types =======================

        httpGetVisualReportTypes() {
            const endpoint = visualReportsEndpoint + "/types";
            return axios({
                method: "get",
                url: endpoint,
                headers: requiredHeaders(),
                data: {},
            })
                .then((response) => {
                    if (response.status === 200) {
                        this.setAvailableVisualReportTypes(response.data.payload || []);
                        return new Promise((resolve) => {
                            resolve(response)
                        })
                    }
                    return apiRequestsHelper.handleResponseWithDialogs(response, false);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpGetVisualReportTypes");
                });
        },

        httpGetVisualReportTypeMetadata(reportType, params) {
            let endpoint = visualReportsEndpoint + "/types/" + reportType;
            if (params && Object.keys(params).length > 0) {
                const query = new URLSearchParams(params).toString();
                endpoint = endpoint + "?" + query;
            }
            return axios({
                method: "get",
                url: endpoint,
                headers: requiredHeaders(),
                data: {},
            })
                .then((response) => {
                    if (response.status === 200) {
                        return new Promise((resolve) => {
                            resolve(response)
                        })
                    }
                    return apiRequestsHelper.handleResponseWithDialogs(response, false);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpGetVisualReportTypeMetadata");
                });
        },

        httpGetVisualReportTypeColumns(reportType) {
            const endpoint = visualReportsEndpoint + "/types/" + reportType + "/columns";
            return axios({
                method: "get",
                url: endpoint,
                headers: requiredHeaders(),
                data: {},
            })
                .then((response) => {
                    return apiRequestsHelper.handleResponseWithDialogs(response, false);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpGetVisualReportTypeColumns");
                });
        },

        httpGetVisualReportTypeFilters(reportType, params) {
            let endpoint = visualReportsEndpoint + "/types/" + reportType + "/filters";
            if (params && Object.keys(params).length > 0) {
                const query = new URLSearchParams(params).toString();
                endpoint = endpoint + "?" + query;
            }
            return axios({
                method: "get",
                url: endpoint,
                headers: requiredHeaders(),
                data: {},
            })
                .then((response) => {
                    if (response.status === 200) {
                        return new Promise((resolve) => {
                            resolve(response)
                        })
                    }
                    return apiRequestsHelper.handleResponseWithDialogs(response, false);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpGetVisualReportTypeFilters");
                });
        },

        // ======================= Saved Reports CRUD =======================

        httpGetVisualReportsPaging(payload) {
            const page = payload.page || 1;
            const size = payload.size || 20;
            let endpoint = visualReportsEndpoint + "?page=" + page + "&size=" + size;
            if (payload.reportType) {
                endpoint = endpoint + "&reportType=" + payload.reportType;
            }
            if (payload.removed !== undefined && payload.removed !== null) {
                endpoint = endpoint + "&removed=" + payload.removed;
            }
            return axios({
                method: "get",
                url: endpoint,
                headers: requiredHeaders(),
                data: {},
            })
                .then((response) => {
                    if (response.status === 200) {
                        this.setVisualReportsList(response.data.payload.content || []);
                        this.setVisualReportsListPagesCount(response.data.payload.totalPages || 0);
                        this.setVisualReportsListTotal(response.data.payload.totalElements || 0);
                    }
                    return apiRequestsHelper.handleResponseWithDialogs(response, false);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpGetVisualReportsPaging");
                });
        },

        httpGetVisualReport(guid) {
            const endpoint = visualReportsEndpoint + "/" + guid;
            return axios({
                method: "get",
                url: endpoint,
                headers: requiredHeaders(),
                data: {},
            })
                .then((response) => {
                    if (response.status === 200) {
                        return new Promise((resolve) => {
                            resolve(response)
                        })
                    }
                    return apiRequestsHelper.handleResponseWithDialogs(response, false);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpGetVisualReport");
                });
        },

        httpCreateVisualReport(payload) {
            let endpoint = visualReportsEndpoint + "?reportType=" + encodeURIComponent(payload.reportType) + "&name=" + encodeURIComponent(payload.name);
            if (payload.description) {
                endpoint = endpoint + "&description=" + encodeURIComponent(payload.description);
            }
            if (payload.creator) {
                endpoint = endpoint + "&creator=" + encodeURIComponent(payload.creator);
            }
            return axios({
                method: "post",
                url: endpoint,
                headers: requiredHeaders(),
                data: {},
            })
                .then((response) => {
                    if (response.status === 200) {
                        return new Promise((resolve) => {
                            resolve(response)
                        })
                    }
                    return apiRequestsHelper.handleResponseWithDialogs(response, true);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpCreateVisualReport");
                });
        },

        httpUpdateVisualReport(guid, payload) {
            let endpoint = visualReportsEndpoint + "/" + guid + "?name=" + encodeURIComponent(payload.name);
            if (payload.description !== undefined && payload.description !== null) {
                endpoint = endpoint + "&description=" + encodeURIComponent(payload.description);
            }
            if (payload.removed !== undefined && payload.removed !== null) {
                endpoint = endpoint + "&removed=" + payload.removed;
            }
            return axios({
                method: "put",
                url: endpoint,
                headers: requiredHeaders(),
                data: {},
            })
                .then((response) => {
                    if (response.status === 200) {
                        return new Promise((resolve) => {
                            resolve(response)
                        })
                    }
                    return apiRequestsHelper.handleResponseWithDialogs(response, true);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpUpdateVisualReport");
                });
        },

        httpDeleteVisualReport(guid, currentName) {
            return this.httpUpdateVisualReport(guid, { name: currentName || "", removed: true });
        },

        // ======================= View Config =======================

        httpGetVisualReportViewConfig(guid, params) {
            let endpoint = visualReportsEndpoint + "/" + guid + "/view-config";
            if (params && Object.keys(params).length > 0) {
                const query = new URLSearchParams(params).toString();
                endpoint = endpoint + "?" + query;
            }
            return axios({
                method: "get",
                url: endpoint,
                headers: requiredHeaders(),
                data: {},
            })
                .then((response) => {
                    if (response.status === 200) {
                        this.setVisualReportViewConfig(response.data.payload);
                        return new Promise((resolve) => {
                            resolve(response)
                        })
                    }
                    return apiRequestsHelper.handleResponseWithDialogs(response, false);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpGetVisualReportViewConfig");
                });
        },

        // ======================= Saved Filters =======================

        httpGetVisualReportSavedFilters(guid) {
            const endpoint = visualReportsEndpoint + "/" + guid + "/saved-filters";
            return axios({
                method: "get",
                url: endpoint,
                headers: requiredHeaders(),
                data: {},
            })
                .then((response) => {
                    if (response.status === 200) {
                        return new Promise((resolve) => {
                            resolve(response)
                        })
                    }
                    return apiRequestsHelper.handleResponseWithDialogs(response, false);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpGetVisualReportSavedFilters");
                });
        },

        httpSaveVisualReportFilters(guid, filters) {
            console.log("httpSaveVisualReportFilters called for guid:", filters);
            const endpoint = visualReportsEndpoint + "/" + guid + "/saved-filters";
            return axios({
                method: "put",
                url: endpoint,
                headers: requiredHeaders(),
                data: filters,
            })
                .then((response) => {
                    if (response.status === 200) {
                        return new Promise((resolve) => {
                            resolve(response)
                        })
                    }
                    return apiRequestsHelper.handleResponseWithDialogs(response, true);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpSaveVisualReportFilters");
                });
        },

        // ======================= Settings =======================

        httpGetVisualReportSettings(guid, params) {
            let endpoint = visualReportsEndpoint + "/" + guid + "/settings";
            if (params && Object.keys(params).length > 0) {
                const query = new URLSearchParams(params).toString();
                endpoint = endpoint + "?" + query;
            }
            return axios({
                method: "get",
                url: endpoint,
                headers: requiredHeaders(),
                data: {},
            })
                .then((response) => {
                    if (response.status === 200) {
                        return new Promise((resolve) => {
                            resolve(response)
                        })
                    }
                    return apiRequestsHelper.handleResponseWithDialogs(response, false);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpGetVisualReportSettings");
                });
        },

        httpSaveVisualReportSettings(guid, settings) {

            const endpoint = visualReportsEndpoint + "/" + guid + "/settings";
            console.log("httpSaveVisualReportSettings called endpoint:",endpoint, settings);
            return axios({
                method: "put",
                url: endpoint,
                headers: requiredHeaders(),
                data: settings,
            })
                .then((response) => {
                    if (response.status === 200) {
                        return new Promise((resolve) => {
                            resolve(response)
                        })
                    }
                    return apiRequestsHelper.handleResponseWithDialogs(response, true);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpSaveVisualReportSettings");
                });
        },

        // ======================= Rows =======================

        httpGetVisualReportRows(guid, payload) {
            const pageNumber = payload.pageNumber || 1;
            const pageSize = payload.pageSize || 20;
            let endpoint = visualReportsEndpoint + "/" + guid + "/rows?pageNumber=" + pageNumber + "&pageSize=" + pageSize;
            if (payload.sortingKey) {
                endpoint = endpoint + "&sortingKey=" + payload.sortingKey;
            }
            if (payload.sortingDirection) {
                endpoint = endpoint + "&sortingDirection=" + payload.sortingDirection;
            }
            return axios({
                method: "post",
                url: endpoint,
                headers: requiredHeaders(),
                data: payload.body || {},
            })
                .then((response) => {
                    if (response.status === 200) {
                        this.setVisualReportRows(response.data.payload.content || []);
                        this.setVisualReportRowsPagesCount(response.data.payload.totalPages || 0);
                        this.setVisualReportRowsTotal(response.data.payload.totalElements || 0);
                    }
                    return apiRequestsHelper.handleResponseWithDialogs(response, false);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpGetVisualReportRows");
                });
        },

        // ======================= Filters fetch method (for CommonTableFilterSection2) =======================

        httpFetchVisualReportFiltersForSection(guid) {
            return (params) => {
                log.log("httpFetchVisualReportFiltersForSection called for guid:", guid, "params:", params);
                return this.httpGetVisualReportViewConfig(guid, params).then((response) => {
                    if (response && response.status === 200 && this.visualReportViewConfig) {
                        // Wrap availableFilters in a format compatible with CommonTableFilterSection2
                        return {
                            status: 200,
                            data: {
                                payload: this.visualReportViewConfig.availableFilters || [],
                            },
                        };
                    }
                    return response;
                });
            };
        },

        // ======================= Chart Config =======================

        httpGetVisualReportChartConfig(reportGuid) {
            const endpoint = visualReportsEndpoint + "/" + reportGuid + "/chart-config";
            return axios({
                method: "get",
                url: endpoint,
                headers: requiredHeaders(),
                data: {},
            })
                .then((response) => {
                    if (response.status === 200) {
                        this.setVisualReportChartConfig(response.data.payload || null);
                        return new Promise((resolve) => {
                            resolve(response);
                        });
                    }
                    return apiRequestsHelper.handleResponseWithDialogs(response, false);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpGetVisualReportChartConfig");
                });
        },

        httpSaveVisualReportChartConfig(reportGuid, payload) {
            const endpoint = visualReportsEndpoint + "/" + reportGuid + "/chart-config";
            return axios({
                method: "put",
                url: endpoint,
                headers: requiredHeaders(),
                data: payload,
            })
                .then((response) => {
                    if (response.status === 200) {
                        this.setVisualReportChartConfig(response.data.payload || null);
                        return new Promise((resolve) => {
                            resolve(response);
                        });
                    }
                    return apiRequestsHelper.handleResponseWithDialogs(response, true);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpSaveVisualReportChartConfig");
                });
        },

        httpGetVisualReportChartData(reportGuid, filters, pageNumber = 0, pageSize = 50) {
            const endpoint = visualReportsEndpoint + "/" + reportGuid + "/chart-data";
            return axios({
                method: "post",
                url: endpoint,
                headers: requiredHeaders(),
                params: {
                    pageNumber,
                    pageSize,
                },
                data: filters || [],
            })
                .then((response) => {
                    if (response.status === 200) {
                        this.setVisualReportChartData(response.data.payload || null);
                        return new Promise((resolve) => {
                            resolve(response);
                        });
                    }
                    return apiRequestsHelper.handleResponseWithDialogs(response, false);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpGetVisualReportChartData");
                });
        },

        httpGetVisualReportAggregationMetadata(reportType) {
            const endpoint = visualReportsEndpoint + "/types/" + reportType + "/aggregation-metadata";
            return axios({
                method: "get",
                url: endpoint,
                headers: requiredHeaders(),
                data: {},
            })
                .then((response) => {
                    if (response.status === 200) {
                        this.setVisualReportAggregationMetadata(response.data.payload || null);
                        return new Promise((resolve) => {
                            resolve(response);
                        });
                    }
                    return apiRequestsHelper.handleResponseWithDialogs(response, false);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpGetVisualReportAggregationMetadata");
                });
        },

        httpGetVisualReportAggregatedRows(reportGuid, payload) {
            const endpoint = visualReportsEndpoint + "/" + reportGuid + "/aggregated-rows";
            return axios({
                method: "post",
                url: endpoint,
                headers: requiredHeaders(),
                data: payload,
            })
                .then((response) => {
                    if (response.status === 200) {
                        this.setVisualReportAggregatedRows(response.data.payload.content || []);
                        this.setVisualReportAggregatedRowsPagesCount(response.data.payload.totalPages || 0);
                        this.setVisualReportAggregatedRowsTotal(response.data.payload.totalElements || 0);
                    }

                    return apiRequestsHelper.handleResponseWithDialogs(response, false);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpGetVisualReportAggregatedRows");
                });
        },

        httpGetVisualReportChartMetadata(reportType) {
            const endpoint = visualReportsEndpoint + "/types/" + reportType + "/chart-metadata";
            return axios({
                method: "get",
                url: endpoint,
                headers: requiredHeaders(),
                data: {},
            })
                .then((response) => {
                    if (response.status === 200) {
                        return new Promise((resolve) => {
                            resolve(response);
                        });
                    }
                    return apiRequestsHelper.handleResponseWithDialogs(response, false);
                })
                .catch((error) => {
                    return apiRequestsHelper.catchResponseError(error, "httpGetVisualReportChartMetadata");
                });
        },
    },
});
